import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import auctionBidderBadge from '@/assets/all-badges/badge_3_auction_bidder.png';
import ShareIcon from '@/assets/all-icons/share-icon';
import useProtectAction from '@/hooks/useProtectAction';
import useStateHandler from '@/hooks/useStateHandler';
import useAuctionCountdown from '@/pages/artwork/hooks/useAuctionCountdown';
import {
  BID_FINAL_PHASE_BUFFER_MS,
  computeMinimumAllowedBid,
  deriveBidIncrement,
  normalizeBidAmount,
} from '@/pages/artwork/utils/bidIncrement.utils';
import UserProfileImage from '@/shared-components/cards/components/UserProfileImage';
import Popup from '@/shared-components/popups/components/Popup';
import NumberUtils from '@/utils/number.utils';

import ArtworkSharePopup from './ArtworkSharePopup';
import AuctionTimeLeftIndicator from './AuctionTimeLeftIndicator';

import './styles/ArtworkAuctionDesktop.css';

/**
 * Dedicated desktop view to handle live auction participation.
 * Mirrors the tone of platforms such as Catawiki: recent bids, quick CTA, and a lean bid form.
 */
export default function ArtworkAuctionDesktop({
  artworkData,
  onBack,
  onPlaceBid,
  isCountdownFrozen = false,
  serverTimeOffsetMs = 0,
  pendingBidState,
  // Allow the gallery's internal view to reuse the layout without exposing bidding controls.
  showBidPanel = true,
  paymentMethods = [],
  selectedPaymentMethodId = null,
  onSelectPaymentMethod = null,
  onReloadPaymentMethods = null,
  isPaymentMethodLoading = false,
  paymentMethodsError = null,
  hasPaymentMethods = false,
  onManagePaymentMethods = null,
  viewMode = 'desktop',
}) {
  const requireAccount = useProtectAction({ typeOfProtection: 'USER_ACCOUNT_REQUIRED' });
  const { state } = useStateHandler();
  const navigate = useNavigate();
  const { artwork_about: artworkAbout = {}, artwork_auction: auction = {} } = artworkData ?? {};
  const isEnabled = Boolean(auction?.is_enabled);
  const currency = artworkAbout.artwork_currency || auction.currency || 'EUR';
  const startingBidRaw =
    auction.price_starting_at ??
    artworkAbout.price_starting_at ??
    artworkAbout.artwork_price ??
    0;
  const startingBidAmount = Number(startingBidRaw) || 0;
  const viewerId = state?.user_session?._id ?? state?.user_session?.id ?? null;
  const resolveBidderId = (candidate) => {
    if (!candidate) {
      return null;
    }
    if (typeof candidate === 'string') {
      return candidate;
    }
    if (typeof candidate === 'object') {
      return candidate._id ?? candidate.id ?? candidate.uid ?? null;
    }
    return null;
  };

  const bids = useMemo(() => {
    const list = Array.isArray(auction.bids) ? auction.bids : [];
    // Sort newest → oldest so the header always reflects the latest movement.
    return [...list].sort((a, b) => {
      // Support both snake_case and camelCase coming from SSE/meta
      const aDate = new Date(a?.created_at ?? a?.placed_at ?? a?.placedAt ?? 0).getTime();
      const bDate = new Date(b?.created_at ?? b?.placed_at ?? b?.placedAt ?? 0).getTime();
      return bDate - aDate;
    });
  }, [auction.bids]);

  const topBidAmount =
    bids[0]?.amount ??
    auction.current_bid_amount ??
    auction.current_bid?.amount ??
    startingBidRaw;

  const formattedTopBid = NumberUtils.getArtworkPrice(
    Number(topBidAmount) || Number(startingBidRaw) || 0,
    currency,
  );
  const formattedStartingBid = NumberUtils.getArtworkPrice(startingBidAmount, currency);

  const [showAllBids, setShowAllBids] = useState(false);
  const [bidValue, setBidValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [inputError, setInputError] = useState(null);
  const bidInputRef = useRef(null);
  const previousTopBidAmountRef = useRef(null);
  const paymentSectionRef = useRef(null);
  const [isSharePopupOpen, setSharePopupOpen] = useState(false);
  const [isRulesPopupOpen, setRulesPopupOpen] = useState(false);
  const [isRulesViewVisible, setRulesViewVisible] = useState(false);
  const isMobileVariant = viewMode === 'mobile';
  const [isBidAnimating, setBidAnimating] = useState(false);
  const [showBidBadge, setShowBidBadge] = useState(false);
  const [paymentFieldError, setPaymentFieldError] = useState(false);
  const formatPaymentMethodLabel = (method) => {
    if (!method || typeof method !== 'object') {
      return 'Saved card';
    }
    const brand = method.brand ? method.brand.toUpperCase() : 'CARD';
    const last4 = method.last4 ?? '????';
    const expMonth =
      typeof method.exp_month === 'number'
        ? String(method.exp_month).padStart(2, '0')
        : method.exp_month ?? null;
    const expYear =
      typeof method.exp_year === 'number'
        ? String(method.exp_year).slice(-2)
        : method.exp_year ?? null;
    const expiry = expMonth && expYear ? `exp ${expMonth}/${expYear}` : null;
    return [brand, `•••• ${last4}`, expiry].filter(Boolean).join(' ');
  };
  const resolvedSelectPaymentMethod =
    typeof onSelectPaymentMethod === 'function' ? onSelectPaymentMethod : () => {};
  const resolvedReloadPaymentMethods =
    typeof onReloadPaymentMethods === 'function' ? onReloadPaymentMethods : () => {};
  const resolvedManagePaymentMethods =
    typeof onManagePaymentMethods === 'function' ? onManagePaymentMethods : () => {};
  const paymentMethodOptions = Array.isArray(paymentMethods) ? paymentMethods : [];
  const hasSelectablePaymentMethods = typeof hasPaymentMethods === 'boolean'
    ? hasPaymentMethods
    : paymentMethodOptions.length > 0;
  const paymentMethodsMessage =
    paymentMethodsError?.error_message ??
    paymentMethodsError?.message ??
    paymentMethodsError?.data?.error_message ??
    null;
  const visibleBids = showAllBids ? bids : bids.slice(0, 6);
  const totalBids = Number(
    auction?.total_bids ??
      auction?.totalBids ??
      auction?.total_bid_count ??
      auction?.totalBidCount ??
      null,
  );
  const hasExistingBids =
    (Array.isArray(auction?.bids) && auction.bids.length > 0) ||
    (Number.isFinite(totalBids) && totalBids > 0) ||
    Number(
      auction?.current_bid_amount ??
        auction?.currentBidAmount ??
        auction?.current_bid_price ??
        auction?.currentBidPrice ??
        auction?.highest_bid_amount ??
        auction?.highestBidAmount ??
        auction?.highest_bid?.amount ??
        auction?.current_bid?.amount ??
        0,
    ) > startingBidAmount;
  const serverMinimumBid = Number(
    auction?.minimum_bid_amount ??
      auction?.minimumBidAmount ??
      auction?.minimum_bid ??
      auction?.minimumBid ??
      0,
  );
  const minimumBidBaseline = useMemo(
    () => Math.max(Number(topBidAmount) || 0, startingBidAmount),
    [topBidAmount, startingBidAmount],
  );
  const { remainingMs: countdownRemainingMs } = useAuctionCountdown(auction?.ends_at, {
    serverTimeOffsetMs,
    freeze: isCountdownFrozen,
  });
  const isFinalTenMinutesPhase =
    typeof countdownRemainingMs === 'number' &&
    countdownRemainingMs > 0 &&
    countdownRemainingMs <= BID_FINAL_PHASE_BUFFER_MS;
  const minimumAllowedBid = useMemo(() => {
    if (Number.isFinite(serverMinimumBid) && serverMinimumBid > 0) {
      return serverMinimumBid;
    }
    if (!hasExistingBids && startingBidAmount > 0) {
      return startingBidAmount;
    }
    return computeMinimumAllowedBid(minimumBidBaseline, {
      preferLowerTier: isFinalTenMinutesPhase,
    });
  }, [
    hasExistingBids,
    isFinalTenMinutesPhase,
    minimumBidBaseline,
    serverMinimumBid,
    startingBidAmount,
  ]);

  const recommendedBids = useMemo(() => {
    if (!Number.isFinite(minimumAllowedBid) || minimumAllowedBid <= 0) {
      return [];
    }

    const step = deriveBidIncrement(minimumAllowedBid, {
      preferLowerTier: isFinalTenMinutesPhase,
    });
    const suggestions = [minimumAllowedBid];
    for (let index = 1; index < 3; index += 1) {
      const rawAmount = minimumAllowedBid + step * index;
      suggestions.push(normalizeBidAmount(rawAmount, step));
    }

    return Array.from(new Set(suggestions.filter((amount) => Number.isFinite(amount) && amount > 0)));
  }, [isFinalTenMinutesPhase, minimumAllowedBid]);

  useEffect(() => {
    const previousAmount = previousTopBidAmountRef.current;
    const normalizedTopBid = Number(topBidAmount);
    previousTopBidAmountRef.current = normalizedTopBid;

    if (!Number.isFinite(normalizedTopBid) || !Number.isFinite(previousAmount)) {
      return;
    }

    if (normalizedTopBid <= previousAmount) {
      return;
    }

    setBidAnimating(true);
    setShowBidBadge(true);
    const pulseTimeout = setTimeout(() => setBidAnimating(false), 500);
    const badgeTimeout = setTimeout(() => setShowBidBadge(false), 3000);
    return () => {
      clearTimeout(pulseTimeout);
      clearTimeout(badgeTimeout);
    };
  }, [topBidAmount]);

  useEffect(() => {
    if (selectedPaymentMethodId) {
      setPaymentFieldError(false);
    }
  }, [selectedPaymentMethodId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const parsedValue = Number(bidValue);
    if (bidValue === '' || !Number.isFinite(parsedValue) || parsedValue <= 0) {
      const message = 'Please enter a bid amount before submitting.';
      setInputError(message);
      setSubmissionError(null);
      return;
    }
    if (parsedValue < minimumAllowedBid) {
      setInputError(
        `The next bid must be at least ${NumberUtils.getArtworkPrice(minimumAllowedBid, currency)}.`,
      );
      setSubmissionError(null);
      return;
    }
    if (!isEnabled) {
      setSubmissionError('This auction is not enabled yet.');
      return;
    }

    if (!selectedPaymentMethodId) {
      setSubmissionError('Select a payment method before placing a bid.');
      setPaymentFieldError(true);
      paymentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      paymentSectionRef.current?.querySelector('select')?.focus();
      return;
    }
    setBidValue('');
    setIsSubmitting(true);
    setSubmissionError(null);
    try {
      if (onPlaceBid) {
        // Defer to the parent so the integration with the auction API can live alongside data fetching.
        await requireAccount(async () => {
          await onPlaceBid({
            amount: parsedValue,
            paymentMethodId: selectedPaymentMethodId,
          });
        });
      }
      setInputError(null);
    } catch (error) {
      console.error('[ARTWORK_AUCTION] Failed to place bid', error);
      const fallbackMessage = 'Unable to place your bid. Please try again.';
      const apiCode = error?.data?.error_type;
      const apiMsg  = error?.data?.error_message || error?.message;
      let message = apiMsg || fallbackMessage;
      if (apiCode === 'ARTWORK_NOT_IN_AUCTION')
      {
        message = 'This auction is not enabled yet.';
      }
      else if (apiCode === 'BID_TOO_LOW' && !apiMsg)
      {
        message = 'Your bid must meet the minimum increment.';
      }
      else if (apiCode === 'ALREADY_HIGHEST_BIDDER')
      {
        message = 'You already hold the highest bid.';
      }
      setSubmissionError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRecommendedSelect = (amount) => {
    setBidValue(String(amount));
    setInputError(null);
    setSubmissionError(null);
    if (bidInputRef.current) {
      bidInputRef.current.focus();
    }
  };

  const handleShareClick = () => {
    // Keep the UI responsive; the popup handles clipboard integrations.
    setSharePopupOpen(true);
  };

  const handleCloseSharePopup = () => {
    setSharePopupOpen(false);
  };

  const handleOpenRulesPopup = () => {
    if (isMobileVariant) {
      setRulesViewVisible(true);
      return;
    }
    setRulesPopupOpen(true);
  };

  const handleCloseRulesPopup = () => {
    if (isMobileVariant) {
      setRulesViewVisible(false);
      return;
    }
    setRulesPopupOpen(false);
  };

  useEffect(() => {
    if (!isMobileVariant && isRulesViewVisible) {
      setRulesViewVisible(false);
    }
  }, [isMobileVariant, isRulesViewVisible]);

  // Surface inline guidance whenever the collector types a value below the allowed minimum.
  useEffect(() => {
    const numericValue = Number(bidValue);
    if (
      bidValue !== '' &&
      Number.isFinite(numericValue) &&
      numericValue >= minimumAllowedBid &&
      inputError
    ) {
      setInputError(null);
    }
  }, [bidValue, inputError, minimumAllowedBid]);

  const bidNumericValue = Number(bidValue);
  const showBidBelowMinimum =
    bidValue !== '' && Number.isFinite(bidNumericValue) && bidNumericValue < minimumAllowedBid;
  const isSubmitDisabled = isSubmitting || !isEnabled;
  const inlineBidMessage = showBidBelowMinimum
    ? `The next bid must be at least ${NumberUtils.getArtworkPrice(minimumAllowedBid, currency)}.`
    : inputError;
  const deriveAuctionState = (value) => {
    if (!value || typeof value !== 'string') {
      return '';
    }
    return value.trim().toLowerCase();
  };

  const auctionState = deriveAuctionState(auction?.state || auction?.status);
  const hasAuctionClosedFlag = Boolean(auction?.is_closed ?? auction?.closed);
  const endsAtTimestamp = auction?.ends_at ? new Date(auction.ends_at).getTime() : NaN;
  const nowTimestamp = Number.isNaN(endsAtTimestamp) ? null : Date.now();
  const hasEndedBySchedule =
    Number.isFinite(endsAtTimestamp) && nowTimestamp !== null && endsAtTimestamp <= nowTimestamp;
  const isAuctionClosed = hasAuctionClosedFlag || auctionState === 'closed' || auctionState === 'ended' || (hasEndedBySchedule && !isEnabled);

  const buildBidSignature = (candidate) => {
    if (!candidate) {
      return null;
    }
    const base = candidate?.bid ?? candidate;
    if (!base) {
      return null;
    }
    const rawId =
      base?.id ??
      base?._id ??
      base?.bid_id ??
      base?.bidId ??
      base?.uuid ??
      null;
    const idempotencyKey =
      base?.idempotency_key ??
      base?.idempotencyKey ??
      null;
    const amountRaw = Number(base?.amount);
    const bidderId =
      base?.bidder_id ??
      base?.bidderId ??
      base?.bidder?._id ??
      base?.bidder?.id ??
      base?.bidder?.user_id ??
      null;
    const bidderUsername =
      base?.bidder_username ??
      base?.bidderUsername ??
      base?.bidder?.user_username ??
      base?.bidder?.username ??
      base?.bidder?.bidder_username ??
      null;

    return {
      id: rawId || null,
      idempotencyKey: idempotencyKey || null,
      amount: Number.isFinite(amountRaw) ? amountRaw : null,
      bidderId: bidderId || null,
      bidderUsername: bidderUsername || null,
    };
  };

  const winnerSource = useMemo(() => {
    const base = auction?.current_bid ?? null;
    if (base && base.amount !== undefined) {
      return base;
    }
    return bids[0] ?? null;
  }, [auction?.current_bid, bids]);

  const normalizedWinner = winnerSource?.bid ?? winnerSource ?? null;
  const winningBidSignature = useMemo(
    () => buildBidSignature(normalizedWinner),
    [normalizedWinner],
  );

  const winnerAmountRaw =
    normalizedWinner?.amount ??
    auction?.highest_bid_amount ??
    artworkAbout.final_bid_price ??
    topBidAmount ??
    startingBidRaw;
  const formattedWinnerAmount = NumberUtils.getArtworkPrice(
    Number(winnerAmountRaw) || Number(topBidAmount) || 0,
    currency,
  );

  const winnerName =
    normalizedWinner?.bidder_username ??
    normalizedWinner?.bidderUsername ??
    normalizedWinner?.bidder?.user_username ??
    normalizedWinner?.bidder?.username ??
    normalizedWinner?.bidder?.name ??
    null;
  const winnerType =
    normalizedWinner?.bidder_type ??
    normalizedWinner?.bidderType ??
    normalizedWinner?.bidder?.user_type ??
    normalizedWinner?.bidder?.type ??
    null;
  const winnerImage =
    // explicit id on bid (new)
    normalizedWinner?.bidder_image_id ??
    normalizedWinner?.bidderImageId ??
    // nested user_image (common)
    normalizedWinner?.bidder?.user_image?.image_id ??
    // flat image_id on bidder (some backends flatten)
    normalizedWinner?.bidder?.image_id ??
    // legacy "bidder_image" sometimes carried the id as a string
    (typeof normalizedWinner?.bidder_image === 'string' ? normalizedWinner?.bidder_image : null) ??
    // gallery/artist variants if present
    normalizedWinner?.bidder?.gallery_image?.image_id ??
    normalizedWinner?.bidder?.artist_image?.image_id ??
    null;

  const closedAtRaw = auction?.closed_at ?? auction?.ends_at ?? null;
  const formattedClosedAt = closedAtRaw
    ? new Date(closedAtRaw).toLocaleString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
    : null;

  const hasWinner = Boolean(normalizedWinner && (winnerName || winnerAmountRaw));
  const winningBidderId =
    winningBidSignature?.bidderId
    ?? resolveBidderId(normalizedWinner?.bidder)
    ?? normalizedWinner?.bidder_id
    ?? normalizedWinner?.bidderId
    ?? null;
  const settlementStatusRaw = auction?.settlement_status ?? auction?.settlementStatus ?? "";
  const settlementStatus = typeof settlementStatusRaw === 'string'
    ? settlementStatusRaw.trim().toLowerCase()
    : "";
  const viewerIsWinner = Boolean(
    winningBidderId &&
    viewerId &&
    String(winningBidderId) === String(viewerId)
  );
  const shouldShowWinnerCta = Boolean(
    isAuctionClosed &&
    viewerIsWinner &&
    (settlementStatus === "" || settlementStatus === "pending" || settlementStatus === "processing")
  );
  const handleOrdersRedirect = () => navigate("/orders/confirmed", { state: { from: true } });
  const shouldRenderBidPanel = showBidPanel || isAuctionClosed;
  const auctionRules = [
    'You must be 18+ to participate in the auction.',
    "To keep bidding fair and smooth, all bidders must verify their payment details before placing a bid. You'll only be charged if you win.",
    "The buyer is responsible for all shipping costs. After winning, you must coordinate shipping directly with MilfShakes. Customs duties or import taxes may also apply and are the responsibility of the winner.",
    'All bids are final and cannot be withdrawn.',
    'Failure to complete payment may result in account restrictions or suspension.',
    'In case of non-payment by the winning bidder, and if the gallery agrees (MilfShakes), the next highest bid may be declared the winner (and so on).',
    'For last-minute bids: if someone outbids you with under 10 minutes left, the timer extends by 5 minutes (up to a maximum of 10 minutes remaining) so everyone gets a fair chance.',
    'By bidding, you agree to these rules and our Terms & Conditions.',
  ];
  const renderAuctionRules = () =>
    auctionRules.map((rule, index) => (
      <p key={`auction-rule-${index}`} className="artwork-auction-desktop__rules-body">
        {rule}
      </p>
    ));

  if (isMobileVariant && isRulesViewVisible) {
    return (
      <div className="artwork-auction-desktop artwork-auction-desktop--rules">
        <div className="artwork-auction-desktop__header">
          <button
            type="button"
            className="artwork-auction-desktop__back"
            onClick={handleCloseRulesPopup}
          >
            <span aria-hidden="true">←</span>
            Back
          </button>
        </div>
        <section className="artwork-auction-desktop__rules-view">
          <h2 className="artwork-auction-desktop__rules-title">Auction Rules</h2>
          {renderAuctionRules()}
        </section>
      </div>
    );
  }

  return (
    <div className="artwork-auction-desktop">
      <div className="artwork-auction-desktop__header">
        <button
          type="button"
          className="artwork-auction-desktop__back"
          onClick={onBack}
        >
          <span aria-hidden="true">←</span>
          Back
        </button>
        <div className="artwork-auction-desktop__header-status">
          <button
            type="button"
            className="artwork-auction-desktop__rules-link"
            onClick={handleOpenRulesPopup}
          >
            Auction Rules
          </button>
          <button
            type="button"
            className="artwork-auction-desktop__share"
            onClick={handleShareClick}
            aria-label="Share this auction"
          >
            <ShareIcon className="artwork-auction-desktop__share-icon" />
          </button>
        </div>
      </div>

      <section className="artwork-auction-desktop__summary">
        <div className="artwork-auction-desktop__summary-header">
          <div className="artwork-auction-desktop__summary-label">Current bid</div>
          <AuctionTimeLeftIndicator
            endsAt={auction.ends_at}
            className="artwork-auction-desktop__timer"
            serverTimeOffsetMs={serverTimeOffsetMs}
            isFrozen={isCountdownFrozen}
          />
        </div>
        <div className="artwork-auction-desktop__summary-value-row">
          <div
            className={`artwork-auction-desktop__summary-value${
              isBidAnimating ? ' artwork-auction-desktop__summary-value--pulse' : ''
            }`}
          >
            {formattedTopBid}
          </div>
          {showBidBadge && (
            <div className="artwork-auction-desktop__summary-badge-wrapper" aria-hidden="true">
              <img
                src={auctionBidderBadge}
                alt=""
                className="artwork-auction-desktop__summary-badge"
              />
            </div>
          )}
        </div>
        <div className="artwork-auction-desktop__summary-meta">
          Starting bid {formattedStartingBid}
        </div>
      </section>

      {shouldRenderBidPanel && (
        <section className={`artwork-auction-desktop__bid-panel${isAuctionClosed ? ' artwork-auction-desktop__bid-panel--closed' : ''}`}>
          {isAuctionClosed ? (
            <>
              <header className="artwork-auction-desktop__bid-header">Auction closed</header>
              {hasWinner ? (
                <div className="artwork-auction-desktop__winner-card">
                  <div className="artwork-auction-desktop__winner-spotlight">
                    <span className="artwork-auction-desktop__winner-badge">Winner</span>
                    <UserProfileImage
                      image={winnerImage || undefined}
                      typeOfProfile={winnerType || undefined}
                      className="artwork-auction-desktop__winner-avatar"
                    />
                  </div>
                  <div className="artwork-auction-desktop__winner-details">
                    <span className="artwork-auction-desktop__winner-name">
                      {winnerName || 'Anonymous collector'}
                    </span>
                    <span className="artwork-auction-desktop__winner-amount">{formattedWinnerAmount}</span>
                    {formattedClosedAt && (
                      <span className="artwork-auction-desktop__winner-meta">Closed {formattedClosedAt}</span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="artwork-auction-desktop__winner-empty">
                  This auction closed without any bids.
                </div>
              )}
              {hasWinner && (
                <p className="artwork-auction-desktop__winner-note">
                  We appreciate everyone who joined. Our team will connect with the winning collector shortly.
                </p>
              )}
              {shouldShowWinnerCta && (
                <div className="artwork-auction-desktop__winner-cta">
                  <p className="artwork-auction-desktop__winner-cta-text">
                    Congratulations! You're the highest bidder. Click continue to manage your order.
                  </p>
                  <button
                    type="button"
                    className="artwork-auction-desktop__winner-cta-button"
                    onClick={handleOrdersRedirect}
                  >
                    Continue
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <header className="artwork-auction-desktop__bid-header">Place your bid</header>
              {recommendedBids.length > 0 && (
                <div className="artwork-auction-desktop__recommended">
                  <span className="artwork-auction-desktop__recommended-label">Recommended bids</span>
                  <div className="artwork-auction-desktop__recommended-list">
                    {recommendedBids.map((amount, index) => (
                      <div
                        key={amount}
                        className="artwork-auction-desktop__recommended-item"
                      >
                        {index === 0 && (
                          <span className="artwork-auction-desktop__recommended-tag">Minimum bid</span>
                        )}
                        <button
                          type="button"
                          className="artwork-auction-desktop__recommended-option"
                          onClick={() => handleRecommendedSelect(amount)}
                        >
                          <span className="artwork-auction-desktop__recommended-amount">
                            {NumberUtils.getArtworkPrice(amount, currency)}
                          </span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <form className="artwork-auction-desktop__bid-form" onSubmit={handleSubmit} noValidate>
                <div className="artwork-auction-desktop__amount-header">
                  <label className="artwork-auction-desktop__bid-label" htmlFor="auction-bid-input">
                    Amount ({currency})
                  </label>
                  <span className="artwork-auction-desktop__recommended-note">IVA included</span>
                </div>
                <div className="artwork-auction-desktop__bid-controls">
                  <input
                    id="auction-bid-input"
                    type="number"
                    min={minimumAllowedBid}
                    step="1"
                    value={bidValue}
                    onChange={(event) => {
                      setBidValue(event.target.value);
                      setInputError(null);
                      setSubmissionError(null);
                    }}
                    className="artwork-auction-desktop__bid-input"
                    ref={bidInputRef}
                    placeholder="Enter your bid"
                  />
                  <button
                    type="submit"
                    className="artwork-auction-desktop__bid-submit"
                    disabled={isSubmitDisabled}
                  >
                    {isSubmitting ? 'Submitting...' : 'Place bid'}
                  </button>
                </div>
                <div className="artwork-auction-desktop__shipping-disclaimer" role="note">
                  <p>
                    The buyer is responsible for coordinating shipping costs directly with MilfShakes after winning the lot.                  </p>
                </div>
                {inlineBidMessage && (
                  <p
                    className="artwork-auction-desktop__bid-hint"
                    role="alert"
                    aria-live="polite"
                  >
                    {inlineBidMessage}
                  </p>
                )}
                {pendingBidState && (
                  <p
                    className={`artwork-auction-desktop__bid-status artwork-auction-desktop__bid-status--${pendingBidState.status}`}
                    role="status"
                    aria-live="polite"
                  >
                    {pendingBidState.message}
                  </p>
                )}
                {submissionError && (
                  <p className="artwork-auction-desktop__bid-error" role="alert">
                    {submissionError}
                  </p>
                )}
              </form>
              <div
                className={`artwork-auction-desktop__payment-section${paymentFieldError ? ' artwork-auction-desktop__payment-section--error' : ''}`}
                ref={paymentSectionRef}
              >
                <div className="artwork-auction-desktop__payment-header">
                  <span className="artwork-auction-desktop__payment-label">Payment method</span>
                  <div className="artwork-auction-desktop__payment-actions">
                    <button
                      type="button"
                      className="artwork-auction-desktop__payment-refresh"
                      onClick={resolvedReloadPaymentMethods}
                      disabled={isPaymentMethodLoading}
                      aria-label="Refresh payment methods"
                    >
                      <span aria-hidden="true" className="artwork-auction-desktop__payment-refresh-icon">
                        ↻
                      </span>
                    </button>
                    <button
                      type="button"
                      className="artwork-auction-desktop__payment-manage"
                      onClick={resolvedManagePaymentMethods}
                    >
                      Add card
                    </button>
                  </div>
                </div>
                {paymentMethodsMessage && (
                  <p className="artwork-auction-desktop__payment-error" role="alert">
                    {paymentMethodsMessage}
                  </p>
                )}
                {hasSelectablePaymentMethods ? (
                  <select
                    className={`artwork-auction-desktop__payment-select${paymentFieldError ? ' artwork-auction-desktop__payment-select--error' : ''}`}
                    value={selectedPaymentMethodId || ''}
                    onChange={(event) => {
                      resolvedSelectPaymentMethod(event.target.value);
                      setPaymentFieldError(false);
                    }}
                  >
                    <option value="" disabled>
                    Select a payment method
                    </option>
                    {paymentMethodOptions.map((method) => (
                      <option key={method.id} value={method.id}>
                        {formatPaymentMethodLabel(method)}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="artwork-auction-desktop__payment-empty">
                    <p>Add a payment method to place a bid.</p>
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      )}

      <section className="artwork-auction-desktop__bids-section">
        <header className="artwork-auction-desktop__bids-header">
          Recent bids
          <span className="artwork-auction-desktop__bids-count">
            {bids.length} total
          </span>
        </header>

        {visibleBids.length > 0 ? (
          <ul className="artwork-auction-desktop__bids-list">
            {visibleBids.map((bid, index) => {
              // Prefer exposing the collector handle; keep older payload keys as a safe fallback.
              const bidderName =
                bid?.bidder_username ??
                bid?.bidderUsername ??
                bid?.bidder?.user_username ??
                bid?.bidder?.username ??
                bid?.bidder?.user_name ??
                bid?.bidder?.name ??
                '';
              const formattedAmount = NumberUtils.getArtworkPrice(Number(bid?.amount) || 0, currency);
              const rawDate = bid?.created_at ?? bid?.placed_at ?? bid?.placedAt ?? bid?.timestamp;
              const formattedDate = rawDate
                ? new Date(rawDate).toLocaleString(undefined, {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  day: '2-digit',
                  month: 'short',
                })
                : '—';
              const bidderProfileType = bid?.bidder_type;
              const bidderImage =
                // explicit id on bid (new)
                bid?.bidder_image_id ??
                // bidder_image subdocument containing the id
                bid?.bidder_image?.image_id ??
                // nested user_image (common)
                bid?.bidder?.user_image?.image_id ??
                // flat image_id on bidder
                bid?.bidder?.image_id ??
                // legacy: bidder_image as plain string
                (typeof bid?.bidder_image === 'string' ? bid?.bidder_image : null) ??
                // other variants (in case)
                bid?.bidder?.gallery_image?.image_id ??
                bid?.bidder?.artist_image?.image_id ??
                null;

              const bidSignature = buildBidSignature(bid);
              const isWinningBid = Boolean(
                winningBidSignature &&
                bidSignature &&
                (
                  (winningBidSignature.id && bidSignature.id && winningBidSignature.id === bidSignature.id) ||
                  (winningBidSignature.idempotencyKey && bidSignature.idempotencyKey && winningBidSignature.idempotencyKey === bidSignature.idempotencyKey) ||
                  (
                    winningBidSignature.amount !== null &&
                    bidSignature.amount !== null &&
                    winningBidSignature.amount === bidSignature.amount &&
                    (
                      (winningBidSignature.bidderId && bidSignature.bidderId && winningBidSignature.bidderId === bidSignature.bidderId) ||
                      (winningBidSignature.bidderUsername && bidSignature.bidderUsername && winningBidSignature.bidderUsername === bidSignature.bidderUsername)
                    )
                  )
                )
              );
              const shouldHighlightWinner = isWinningBid || (!winningBidSignature && index === 0);

              return (
                <li
                  key={`${bid?.id ?? index}-${rawDate ?? index}`}
                  className={`artwork-auction-desktop__bid-item${shouldHighlightWinner ? ' artwork-auction-desktop__bid-item--winner' : ''}`}
                  aria-current={shouldHighlightWinner ? 'true' : undefined}
                >
                  <UserProfileImage
                    image={bidderImage || undefined}
                    typeOfProfile={bidderProfileType || undefined}
                    className="artwork-auction-desktop__bidder-avatar"
                  />
                  <div className="artwork-auction-desktop__bid-content">
                    <div className="artwork-auction-desktop__bidder">
                      <span className="artwork-auction-desktop__bidder-name">{bidderName}</span>
                      <span className="artwork-auction-desktop__bidder-time">{formattedDate}</span>
                    </div>
                    <span className="artwork-auction-desktop__bid-amount">{formattedAmount}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="artwork-auction-desktop__empty-state">
            No bids yet. Be the first collector to participate.
          </p>
        )}

        {bids.length > 6 && !showAllBids && (
          <button
            type="button"
            className="artwork-auction-desktop__view-all"
            onClick={() => setShowAllBids(true)}
          >
            View all bids
          </button>
        )}
      </section>
      {!isMobileVariant && (
        <Popup
          title="Auction Rules"
          open={isRulesPopupOpen}
          close={handleCloseRulesPopup}
          className="artwork-auction-desktop__rules-popup"
          backgroundClassName="artwork-auction-desktop__rules-overlay"
        >
          {renderAuctionRules()}
        </Popup>
      )}
      <ArtworkSharePopup
        open={isSharePopupOpen}
        onClose={handleCloseSharePopup}
        artworkData={artworkData}
        auctionData={auction}
      />
    </div>
  );
}
